/**
 * Per-device GPS trail + video-coverage hook for the public viewer.
 *
 * v2 — accepts a multi-session "run" so a device that dropped mid-stage
 * and reconnected renders as one stitched polyline, and an optional
 * raceline context so the trail trims to the organizer-defined window
 * (samples whose foot-of-perpendicular distance to the raceline exceeds
 * `maxOffsetM`, or whose s-coordinate is outside `[0, totalLengthM]`,
 * are dropped).
 *
 * Internals:
 *   - REST seed: full-session GPS history via `api.listSessionTelemetry`
 *     for each constituent session (parallel), concat, RDP-simplified
 *     to ≤4k points so the polyline stays fast on multi-hour runs.
 *   - Live extension: `useLiveGpsStream` is attached only to the *last*
 *     session in the run (older sessions are finished and frozen).
 *     Appends SSE samples newer than the seed's last timestamp.
 *   - Coverage: `api.getSessionCoverage` polled per constituent session
 *     on run change + every 30 s, then sweep-merged into one union.
 *     A heuristic re-poll fires (throttled) when an SSE sample arrives
 *     outside the current union.
 *
 * Per-run cache so reopening the same pin within the session is instant.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import api from '../services/api';
import { useLiveGpsStream } from '../plugins/synced_viewer/useLiveGpsStream';
import {
  isCovered,
  mergeCoverageRanges,
  nearestPointOnPolyline,
  simplifyTrackRDP,
} from '../plugins/synced_viewer/util';
import {
  FleetEvent,
  GpsSummary,
  SessionTelemetrySample,
} from '../types';

interface CachedSeed {
  /** Constituent session ids in the order they were fetched (oldest → newest). */
  sessionIds: string[];
  seed: GpsSummary['track'];
  lastSeedUtcMs: number;
}

export interface DeviceTrailTarget {
  /** Ordered constituent session ids (oldest → newest). Live SSE attaches
   * to the last one; earlier ones are treated as finished. */
  sessions: string[];
}

export interface RacelineTrimContext {
  /** Polyline vertices in [lat, lon] order. */
  coords: Array<[number, number]>;
  cumulativeS: number[];
  totalLengthM: number;
  /** Drop samples whose perpendicular distance to the raceline exceeds
   * this (meters). Default 50 m. */
  maxOffsetM?: number;
}

const COVERAGE_REPOLL_MS = 30_000;
const COVERAGE_HEURISTIC_THROTTLE_MS = 10_000;
const SEED_TELEMETRY_LIMIT = 50_000;
const MAX_TRACK_POINTS = 4096;
const DEFAULT_MAX_OFFSET_M = 50;

export interface UseDeviceTrailResult {
  track: GpsSummary['track'];
  coverage: Array<[number, number]>;
  loading: boolean;
}

/** A fix whose capture time trails its server arrival by more than
 *  this is Android's cached last-known-location (stale position +
 *  timestamp) handed to a fresh GPS listener — drawing it paints a
 *  kilometers-long straight ghost line from wherever the phone last
 *  had a fix. Old APKs predate the phone-side guard. */
const STALE_FIX_MAX_AGE_MS = 600_000;

function telemetryToTrack(
  samples: SessionTelemetrySample[],
): GpsSummary['track'] {
  const track: GpsSummary['track'] = [];
  let lastTs = -Infinity;
  for (const s of samples) {
    const g = s.gps;
    if (!g || g.lat == null || g.lon == null) continue;
    const ts = g.fix_ts_ms ?? s.ts_ms;
    if (typeof ts !== 'number' || !Number.isFinite(ts)) continue;
    if (
      typeof g.fix_ts_ms === 'number' &&
      typeof s.ts_ms === 'number' &&
      s.ts_ms - g.fix_ts_ms > STALE_FIX_MAX_AGE_MS
    ) {
      continue; // stale cached fix
    }
    if (ts <= lastTs) continue;
    lastTs = ts;
    track.push([g.lat, g.lon, ts, g.speed_mps ?? 0]);
  }
  return track;
}

function trimToRaceline(
  track: GpsSummary['track'],
  ctx: RacelineTrimContext,
): GpsSummary['track'] {
  const maxOffset = ctx.maxOffsetM ?? DEFAULT_MAX_OFFSET_M;
  if (track.length === 0 || ctx.coords.length < 2) return track;
  const out: GpsSummary['track'] = [];
  for (const p of track) {
    const proj = nearestPointOnPolyline(ctx.coords, ctx.cumulativeS, p[0], p[1]);
    if (!proj) continue;
    if (proj.d_m > maxOffset) continue;
    if (proj.s_m < 0 || proj.s_m > ctx.totalLengthM) continue;
    out.push(p);
  }
  return out;
}

function normalizeTarget(target: DeviceTrailTarget | string | null | undefined): DeviceTrailTarget | null {
  if (target == null) return null;
  if (typeof target === 'string') return { sessions: [target] };
  if (!target.sessions || target.sessions.length === 0) return null;
  return target;
}

function targetKey(target: DeviceTrailTarget | null): string {
  return target ? target.sessions.join('|') : '';
}

export function useDeviceTrail(
  target: DeviceTrailTarget | string | null,
  racelineCtx?: RacelineTrimContext | null,
): UseDeviceTrailResult {
  const normalized = useMemo(() => normalizeTarget(target), [target]);
  const key = useMemo(() => targetKey(normalized), [normalized]);
  const liveSessionId = normalized?.sessions[normalized.sessions.length - 1] ?? null;

  const [seed, setSeed] = useState<CachedSeed | null>(null);
  const [coverage, setCoverage] = useState<Array<[number, number]>>([]);
  const [loading, setLoading] = useState(false);

  // Per-run caches so reopening the same pin is instant.
  const seedCacheRef = useRef<Map<string, CachedSeed>>(new Map());
  const coverageCacheRef = useRef<Map<string, Array<[number, number]>>>(new Map());
  const lastHeuristicCallRef = useRef<number>(0);

  // ── REST seed (parallel per-session telemetry, then concat + RDP) ──
  useEffect(() => {
    if (!normalized) {
      setSeed(null);
      setCoverage([]);
      return;
    }
    const cached = seedCacheRef.current.get(key);
    if (cached) {
      setSeed(cached);
      const cachedCov = coverageCacheRef.current.get(key);
      if (cachedCov) setCoverage(cachedCov);
    } else {
      setSeed(null);
      setCoverage([]);
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const batches = await Promise.all(
          normalized.sessions.map((sid) =>
            api.listSessionTelemetry(sid, {
              kind: 'gps',
              limit: SEED_TELEMETRY_LIMIT,
            }),
          ),
        );
        if (cancelled) return;
        const merged: SessionTelemetrySample[] = [];
        for (const batch of batches) {
          for (const s of batch) merged.push(s);
        }
        merged.sort((a, b) => {
          const at = a.gps?.fix_ts_ms ?? a.ts_ms ?? 0;
          const bt = b.gps?.fix_ts_ms ?? b.ts_ms ?? 0;
          return at - bt;
        });
        const raw = telemetryToTrack(merged);
        // Full capture rate (1 Hz): the trail polyline, playhead car
        // dot, click-to-seek and glass-rail speed all read this track,
        // and the old 3 m RDP tolerance cut corners + spaced vertices
        // 5-15 s apart (field report 2026-06-11 — same finding as the
        // server-side summary, fixed in PR #62). Simplify only when a
        // multi-hour run exceeds the polyline budget; the util's 0.1 m
        // epsilon floor then sheds only sub-GPS-noise collinearity
        // before its uniform-downsample fallback enforces the cap.
        const simplified =
          raw.length <= MAX_TRACK_POINTS
            ? raw
            : simplifyTrackRDP(raw, 0.1, MAX_TRACK_POINTS);
        const lastSeedUtcMs = simplified.length
          ? simplified[simplified.length - 1][2]
          : -Infinity;
        const entry: CachedSeed = {
          sessionIds: normalized.sessions,
          seed: simplified,
          lastSeedUtcMs,
        };
        seedCacheRef.current.set(key, entry);
        setSeed(entry);
      } catch (err) {
        console.warn('[useDeviceTrail] seed fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [normalized, key]);

  // ── Coverage union across all constituent sessions ──
  const refreshCoverage = useCallback(async (sessions: string[], k: string) => {
    try {
      const perSession = await Promise.all(
        sessions.map(async (sid) => {
          try {
            const cov = await api.getSessionCoverage(sid);
            return (cov.union || []).map(
              (r) => [r[0], r[1]] as [number, number],
            );
          } catch {
            return [] as Array<[number, number]>;
          }
        }),
      );
      const flat = perSession.flat();
      const union = mergeCoverageRanges(flat);
      coverageCacheRef.current.set(k, union);
      setCoverage(union);
    } catch (err) {
      console.warn('[useDeviceTrail] coverage fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (!normalized) return;
    void refreshCoverage(normalized.sessions, key);
    const id = window.setInterval(() => {
      void refreshCoverage(normalized.sessions, key);
    }, COVERAGE_REPOLL_MS);
    return () => window.clearInterval(id);
  }, [normalized, key, refreshCoverage]);

  // ── Live GPS extension via SSE — only the last session is "live". ──
  const live = useLiveGpsStream(liveSessionId ?? undefined, !!liveSessionId);

  // ── Heuristic coverage re-poll on new live samples outside current union ──
  useEffect(() => {
    if (!normalized || !liveSessionId) return;
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type !== 'device_telemetry') return;
      if (evt.session_id !== liveSessionId) return;
      const g = evt.gps;
      if (!g || g.lat == null || g.lon == null) return;
      const ts = g.fix_ts_ms ?? (evt.ts != null ? evt.ts * 1000 : Date.now());
      if (typeof ts !== 'number' || !Number.isFinite(ts)) return;
      const currentCov = coverageCacheRef.current.get(key) ?? [];
      if (isCovered(ts, currentCov)) return;
      const now = Date.now();
      if (now - lastHeuristicCallRef.current < COVERAGE_HEURISTIC_THROTTLE_MS) return;
      lastHeuristicCallRef.current = now;
      void refreshCoverage(normalized.sessions, key);
    });
    return unsub;
  }, [normalized, liveSessionId, key, refreshCoverage]);

  // ── Compose seed + live into a single monotonic track ──
  const fullTrack = useMemo<GpsSummary['track']>(() => {
    const seedTrack = seed?.seed ?? [];
    const liveTrack = live.liveSummary?.track ?? [];
    if (liveTrack.length === 0) return seedTrack;
    if (seedTrack.length === 0) return liveTrack;
    const cutoff = seed?.lastSeedUtcMs ?? -Infinity;
    const appended: GpsSummary['track'] = [];
    for (const p of liveTrack) {
      if (p[2] > cutoff) appended.push(p);
    }
    if (appended.length === 0) return seedTrack;
    return seedTrack.concat(appended);
  }, [seed, live.liveSummary]);

  // ── Optional raceline trim ──
  const track = useMemo<GpsSummary['track']>(() => {
    if (!racelineCtx || racelineCtx.coords.length < 2) return fullTrack;
    return trimToRaceline(fullTrack, racelineCtx);
  }, [fullTrack, racelineCtx]);

  return { track, coverage, loading };
}

export default useDeviceTrail;
